/**
 * The TanStack Table v9 feature set for every table in the app. Registering it once keeps all
 * tables on the state shape `usePersistedTableState` manages.
 */

import {
  columnFacetingFeature,
  columnFilteringFeature,
  columnGroupingFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createExpandedRowModel,
  createFacetedRowModel,
  createFacetedUniqueValues,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  createTableHook,
  globalFilteringFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowPinningFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  tableFeatures,
  type AccessorColumnDef,
  type Cell,
  type CellData,
  type Column,
  type ColumnDef,
  type FilterFn,
  type Header,
  type ReactTable,
  type Row,
  type RowData,
  type TableOptions,
  type TableState,
} from "@tanstack/react-table";
import { alternateSortingFeature } from "./features/alternateSorting";
import { selectAllFeature } from "./features/selectAll";

const appTableFeatures = tableFeatures({
  columnFilteringFeature,
  globalFilteringFeature,
  rowSortingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowExpandingFeature,
  rowPinningFeature,
  columnPinningFeature,
  columnVisibilityFeature,
  columnOrderingFeature,
  columnSizingFeature,
  columnResizingFeature,
  columnGroupingFeature,
  columnFacetingFeature,

  // Must stay after the built-ins they override/wrap.
  alternateSortingFeature,
  selectAllFeature,

  // `grouping` is a server-side dimension selector, so no grouped row model.
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  expandedRowModel: createExpandedRowModel(),
  facetedRowModel: createFacetedRowModel(),
  facetedUniqueValues: createFacetedUniqueValues(),

  sortFns: { alphanumeric: sortFn_alphanumeric },
});

type AppTableFeatures = typeof appTableFeatures;

/** `columnResizing` updates per pointer move; `Table.tsx` reads it via `table.atoms` instead. */
type AppSelectedTableState = Omit<TableState<AppTableFeatures>, "columnResizing">;

/** `TFeatures` bound once, so props keep annotating with a single type argument. */
export type AppTable<TData extends RowData, TSelected = AppSelectedTableState> = ReactTable<
  AppTableFeatures,
  TData,
  TSelected
>;
export type AppRow<TData extends RowData> = Row<AppTableFeatures, TData>;
export type AppColumn<TData extends RowData, TValue extends CellData = CellData> = Column<
  AppTableFeatures,
  TData,
  TValue
>;
export type AppColumnDef<TData extends RowData, TValue extends CellData = CellData> = ColumnDef<
  AppTableFeatures,
  TData,
  TValue
>;
export type AppCell<TData extends RowData, TValue extends CellData = CellData> = Cell<AppTableFeatures, TData, TValue>;
export type AppHeader<TData extends RowData, TValue extends CellData = CellData> = Header<
  AppTableFeatures,
  TData,
  TValue
>;
export type AppAccessorColumnDef<TData extends RowData, TValue extends CellData = CellData> = AccessorColumnDef<
  AppTableFeatures,
  TData,
  TValue
>;
export type AppTableState = TableState<AppTableFeatures>;
export type AppTableOptions<TData extends RowData> = TableOptions<AppTableFeatures, TData>;
export type AppFilterFn<TData extends RowData> = FilterFn<AppTableFeatures, TData>;

/**
 * A whole-table column list. `ColumnDef` behaves invariantly in `TValue` (its `cell`/`header`
 * templates take it as a parameter), so mixed-value columns have no common element type — the
 * hole upstream's `columnHelper.columns()` plugs the same way. Keep the precise
 * `AppColumnDef<TData, TValue>` for a single column.
 */
// oxlint-disable-next-line typescript/no-explicit-any
export type AppColumns<TData extends RowData> = ReadonlyArray<ColumnDef<AppTableFeatures, TData, any>>;

/**
 * v9's `useTable` returns `{ ...table, options: tableOptions }` — the options the caller passed,
 * not the instance's options with feature defaults merged in. So any `table.options.x` read in a
 * component sees `undefined` for an option nobody passed, where v8 saw the default. Restating the
 * defaults the app reads keeps those reads working; extend this if a component starts reading
 * another defaulted option.
 */
const appTableHook = createTableHook({
  features: appTableFeatures,
  enableRowSelection: false,
  enableMultiRowSelection: true,
  columnResizeMode: "onEnd",
  columnResizeDirection: "ltr",
});

type AppTableOptionsInput<TData extends RowData> = Omit<AppTableOptions<TData>, "features" | "columns"> & {
  columns: AppColumns<TData>;
};

type ProcessingOptions = "rowCount" | "manualPagination" | "manualSorting" | "manualFiltering";

type AppTableInput<TData extends RowData> = Omit<AppTableOptionsInput<TData>, "data" | ProcessingOptions> & {
  data: ReadonlyArray<TData> | undefined;
};

const noRows: never[] = [];

// Column filters are the API's. A function also stops v9 resolving "auto" (a row scan and dev warning).
const keepEveryRow = () => true;

const selectAppTableState = ({ columnResizing: _columnResizing, ...rest }: AppTableState): AppSelectedTableState =>
  rest;

/** `data` is one page the API already filtered, sorted and sliced. */
export function useServerTable<TData extends RowData>({
  data,
  rowCount,
  defaultColumn,
  ...options
}: AppTableInput<TData> & { rowCount: number | undefined }) {
  return appTableHook.useAppTable<TData, AppSelectedTableState>(
    {
      ...options,
      data: data ?? noRows,
      rowCount,
      defaultColumn: { filterFn: keepEveryRow, ...defaultColumn },
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
    },
    selectAppTableState,
  );
}

/** `data` is the whole dataset. It pages only when `state` or `initialState` sets up `pagination`. */
export function useClientTable<TData extends RowData>({ data, ...options }: AppTableInput<TData>) {
  const paginated = !!(options.state?.pagination ?? options.initialState?.pagination);
  return appTableHook.useAppTable<TData, AppSelectedTableState>(
    { ...options, data: data ?? noRows, manualPagination: !paginated },
    selectAppTableState,
  );
}

export const createAppColumnHelper = appTableHook.createAppColumnHelper;
